import { X } from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { createPortal } from "react-dom";
import { useTiltScout } from "../hooks/useTiltScout";
import type { IVScoreResponse } from "../types/market-data";
import { TiltScoutFuseVisual } from "./narrative/TiltScoutFuseVisual";
import {
  ARROW_FUSE_GAP_PX,
  ARROW_SLOT_WIDTH_PX,
  FUSE_WIDTH_PX,
} from "./narrative/tilt-scout-fuse-utils";
import { normalizeTiltScoutReadLabel } from "./narrative/tilt-scout-copy";
import type { TiltScoutSnapshot } from "./narrative/tilt-scout-types";
import { FintheonNumericText } from "./numbers/FintheonNumericText";

interface MobileIvScoringDrawerProps {
  data: IVScoreResponse;
  open: boolean;
  onClose: () => void;
}

const MOBILE_IV_SWIPE_DISMISS_PX = 86;
const MOBILE_IV_SWIPE_FAST_DISMISS_PX = 38;
const MOBILE_IV_SWIPE_FAST_DISMISS_VELOCITY = 0.55;
const MOBILE_IV_CLOSE_MS = 260;
type MobileIvCloseOptions = {
  preserveDragOffset?: boolean;
};

export function MobileIvScoringDrawer({
  data,
  open,
  onClose,
}: MobileIvScoringDrawerProps) {
  const { snapshot, error } = useTiltScout(data.instrument);
  const readLabel = normalizeTiltScoutReadLabel(snapshot.label);
  const hasLiveDeskWindow = snapshot.deskPlanPreview?.state === "live";
  const scoreSummary = buildTiltScoutScoreSummary(snapshot, error);
  const sheetRef = useRef<HTMLElement | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dragPointerIdRef = useRef<number | null>(null);
  const dragStartYRef = useRef(0);
  const dragStartTimeRef = useRef(0);
  const isDraggingRef = useRef(false);
  const isDismissingRef = useRef(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);

  const requestClose = useCallback(
    (options: MobileIvCloseOptions = {}) => {
      if (isDismissingRef.current) return;
      if (!options.preserveDragOffset) setDragOffset(0);
      setIsDragging(false);
      isDraggingRef.current = false;
      dragPointerIdRef.current = null;
      isDismissingRef.current = true;
      setIsDismissing(true);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      closeTimerRef.current = setTimeout(onClose, MOBILE_IV_CLOSE_MS);
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return;
    setDragOffset(0);
    setIsDragging(false);
    setIsDismissing(false);
    isDraggingRef.current = false;
    isDismissingRef.current = false;
    dragPointerIdRef.current = null;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") requestClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, requestClose]);

  useEffect(
    () => () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    },
    [],
  );

  const handlePointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    if (isDismissing) return;
    if (event.pointerType === "mouse" && event.button !== 0) return;
    dragPointerIdRef.current = event.pointerId;
    dragStartYRef.current = event.clientY;
    dragStartTimeRef.current = performance.now();
    isDraggingRef.current = false;
    setIsDragging(false);
    setDragOffset(0);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const finishPointerDrag = (event: ReactPointerEvent<HTMLElement>) => {
    if (dragPointerIdRef.current !== event.pointerId) return;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // Pointer capture can already be gone if the browser cancels the touch.
    }
    const elapsed = Math.max(performance.now() - dragStartTimeRef.current, 1);
    const velocity = dragOffset / elapsed;
    const shouldDismiss =
      dragOffset >= MOBILE_IV_SWIPE_DISMISS_PX ||
      (dragOffset >= MOBILE_IV_SWIPE_FAST_DISMISS_PX &&
        velocity >= MOBILE_IV_SWIPE_FAST_DISMISS_VELOCITY);

    dragPointerIdRef.current = null;
    isDraggingRef.current = false;

    if (shouldDismiss) {
      setDragOffset(Math.max(dragOffset, 140));
      requestClose({ preserveDragOffset: true });
      return;
    }

    setDragOffset(0);
    setIsDragging(false);
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    if (dragPointerIdRef.current !== event.pointerId || isDismissing) return;
    const deltaY = event.clientY - dragStartYRef.current;
    if (deltaY <= 0) {
      if (isDraggingRef.current) setDragOffset(0);
      return;
    }

    const sheet = sheetRef.current;
    const canPullDrawer =
      !sheet || sheet.scrollTop <= 0 || isDraggingRef.current;
    if (!canPullDrawer) return;
    if (deltaY < 6 && !isDraggingRef.current) return;

    event.preventDefault();
    isDraggingRef.current = true;
    setIsDragging(true);
    setDragOffset(Math.min(deltaY, 220));
  };

  if (!open) return null;

  return createPortal(
    <div
      id="mobile-iv-scoring-drawer"
      className="mobile-iv-scoring-drawer fixed inset-0 z-[10000]"
      data-testid="mobile-iv-scoring-drawer"
      data-dismissing={isDismissing || undefined}
      role="dialog"
      aria-modal="true"
      aria-label="Agentic scoring breakdown"
    >
      <button
        type="button"
        className="mobile-iv-scoring-backdrop absolute inset-0 bg-black/36 backdrop-blur-[2px]"
        aria-label="Close IV breakdown"
        onClick={() => requestClose()}
      />
      <section
        ref={sheetRef}
        className="mobile-iv-scoring-sheet absolute inset-x-0 bottom-0 max-h-[82vh] overflow-y-auto rounded-t-[30px] border border-b-0 border-[var(--fintheon-accent)]/24 bg-[#090907]/96 px-5 pb-[calc(env(safe-area-inset-bottom)+1.1rem)] pt-3 shadow-[0_-18px_60px_rgba(0,0,0,0.58)]"
        data-dragging={isDragging || undefined}
        data-dismissing={isDismissing || undefined}
        style={
          {
            "--mobile-iv-drawer-drag-y": `${dragOffset}px`,
          } as CSSProperties
        }
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishPointerDrag}
        onPointerCancel={finishPointerDrag}
      >
        <div className="mobile-iv-scoring-grabber mx-auto mb-3 h-1 w-10 rounded-full bg-[var(--fintheon-muted)]/28" />
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <p className="font-mono text-[12px] uppercase tracking-[0.18em] text-[var(--fintheon-accent)]">
              Agentic Scoring
            </p>
            <h2 className="mt-0.5 text-sm font-semibold text-[var(--fintheon-text)]/88">
              IV Breakdown
            </h2>
          </div>
          <button
            type="button"
            className="grid h-8 w-8 place-items-center rounded-full text-[var(--fintheon-muted)] transition hover:text-[var(--fintheon-accent)]"
            aria-label="Close IV breakdown"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              requestClose();
            }}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        <NextSessionForecast data={data} />

        <section className="mt-4 rounded-2xl border border-[var(--fintheon-accent)]/16 bg-[var(--fintheon-bg)]/58 px-4 py-4 text-center">
          <div className="mx-auto flex w-full max-w-[230px] flex-col items-center text-center">
            <p className="w-full text-center text-[10px] uppercase tracking-[0.18em] text-[var(--fintheon-primary)]">
              TiltScout
            </p>
            <div
              className="mt-3 grid items-center justify-center"
              style={{
                columnGap: ARROW_FUSE_GAP_PX,
                gridTemplateColumns: `${ARROW_SLOT_WIDTH_PX}px ${FUSE_WIDTH_PX}px ${ARROW_SLOT_WIDTH_PX}px`,
                width:
                  FUSE_WIDTH_PX +
                  ARROW_SLOT_WIDTH_PX * 2 +
                  ARROW_FUSE_GAP_PX * 2,
              }}
            >
              <TiltScoutFuseVisual
                score={snapshot.score}
                posture={snapshot.posture}
              />
            </div>
            <p className="mt-3 flex w-[5.75ch] justify-center text-center font-mono text-xl tabular-nums text-[var(--fintheon-accent)]">
              <FintheonNumericText
                text={formatTiltScore(snapshot.score)}
                kind="score"
              />
            </p>
            <p className="mt-1 w-full text-center text-xs font-medium text-[var(--fintheon-text)]/84">
              {readLabel}
            </p>
          </div>
        </section>

        <section className="mt-3 rounded-2xl border border-zinc-800/80 bg-zinc-950/58 px-4 py-4 text-center">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--fintheon-primary)]">
            TiltScout Info
          </p>
          <p className="mx-auto mt-2 max-w-[19rem] text-sm leading-snug text-[var(--fintheon-text)]/86">
            {formatDeskEventText(snapshot.eventText)}
          </p>
          {hasLiveDeskWindow && (
            <div className="mx-auto mt-3 grid max-w-[19rem] gap-2 text-[11px] text-[var(--fintheon-muted)]">
              <CenteredFact label="Range" value={snapshot.rangeText} />
              <CenteredFact
                label="Invalidation"
                value={snapshot.invalidationText}
              />
            </div>
          )}
          <p className="mx-auto mt-3 max-w-[19rem] text-[11px] leading-snug text-[var(--fintheon-muted)]/78">
            <FintheonNumericText text={scoreSummary} kind="score" />
          </p>
        </section>
      </section>
    </div>,
    document.body,
  );
}

function formatDeskEventText(text: string) {
  return text === "Desk Plan pending"
    ? "Next planned Desk event upcoming."
    : text;
}

function buildTiltScoutScoreSummary(
  snapshot: TiltScoutSnapshot,
  error: string | null,
) {
  const packetSummary = snapshot.summary.trim();
  const rawEvidenceTitle = snapshot.topEvidence?.title?.trim();
  if (
    packetSummary &&
    (!rawEvidenceTitle || !packetSummary.includes(rawEvidenceTitle))
  ) {
    return packetSummary;
  }

  const scoreText = `${snapshot.score > 0 ? "+" : ""}${snapshot.score.toFixed(1)}`;
  const driver = snapshot.axisScores
    .filter((axis) => axis.id !== "overall" && axis.sourceCount > 0)
    .sort((a, b) => Math.abs(b.score) - Math.abs(a.score))[0];
  const driverText = driver
    ? `${driver.label.toLowerCase()} pressure is the strongest contributor`
    : snapshot.topEvidence
      ? "RiskFlow catalyst pressure is the strongest contributor"
      : "source coverage is still thin";
  const deskText =
    snapshot.deskPlanPreview?.state === "live"
      ? "desk timing is in scope"
      : "desk timing is not contributing yet";

  if (driverText || deskText) {
    return `TiltScout is ${snapshot.posture} at ${scoreText}: ${driverText}; ${deskText}.`;
  }

  return error ?? "TiltScout synthesis pending.";
}

function NextSessionForecast({ data }: { data: IVScoreResponse }) {
  const prediction = data.prediction;
  if (!prediction) {
    return (
      <section className="rounded-2xl border border-[var(--fintheon-accent)]/16 bg-[var(--fintheon-surface)]/72 px-4 py-4 text-center">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--fintheon-primary)]">
          Next Session Forecast
        </p>
        <p className="mt-2 text-sm text-[var(--fintheon-muted)]">
          Forecast pending.
        </p>
      </section>
    );
  }

  return (
    <section className="rounded-2xl border border-[var(--fintheon-accent)]/16 bg-[var(--fintheon-surface)]/72 px-4 py-4 text-center">
      <div className="flex items-center justify-center gap-2">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--fintheon-primary)]">
          Next Session Forecast
        </p>
        <span className="rounded-full bg-zinc-900 px-2 py-0.5 text-[8px] uppercase tracking-[0.12em] text-zinc-500">
          {prediction.source === "agentDesk" ? "Agent Desk" : "Heuristic"}
        </span>
      </div>
      <p className="mt-3 font-mono text-3xl font-semibold tabular-nums text-[var(--fintheon-accent)]">
        <FintheonNumericText
          text={String(prediction.nextSessionScore.toFixed(1))}
        />
      </p>
      <p className="mt-1 text-[11px] text-[var(--fintheon-muted)]">
        Confidence{" "}
        <FintheonNumericText
          text={String((prediction.confidence * 100).toFixed(0))}
        />
        % / Regime shift{" "}
        <FintheonNumericText
          text={String((prediction.regimeShiftProbability * 100).toFixed(0))}
        />
        %
      </p>
      {prediction.scenarios.length > 0 ? (
        <div className="mt-3 grid gap-1.5">
          {prediction.scenarios.slice(0, 3).map((scenario) => (
            <div
              key={scenario.label}
              className="grid grid-cols-[minmax(0,1fr)_2.4rem_2.2rem] items-center gap-2 rounded-lg bg-black/18 px-3 py-1.5 text-[10px]"
            >
              <span className="truncate text-left text-[var(--fintheon-text)]/70">
                {scenario.label}
              </span>
              <span className="text-right font-mono text-[var(--fintheon-muted)]">
                <FintheonNumericText
                  text={String((scenario.probability * 100).toFixed(0))}
                />
                %
              </span>
              <span className="text-right font-mono text-[var(--fintheon-accent)]">
                <FintheonNumericText
                  text={String(scenario.projectedScore.toFixed(1))}
                />
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function CenteredFact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-[0.16em] text-[var(--fintheon-primary)]/90">
        {label}
      </p>
      <p className="mt-0.5 leading-snug text-[var(--fintheon-text)]/74">
        <FintheonNumericText text={value} />
      </p>
    </div>
  );
}

function formatTiltScore(score: number) {
  return `${score > 0 ? "+" : ""}${score.toFixed(1)}`;
}
